import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Repeat, Shield, ArrowRight, ArrowLeft, BadgeCheck, Headphones,
  DollarSign, Clock, CreditCard, Smartphone, CheckCircle, TrendingUp,
  AlertCircle, Loader2, Wallet, Copy, Mail
} from 'lucide-react';import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COUNTRY_CODES = [
  { code: 'CA', dial: '+1', flagUrl: 'https://flagcdn.com/w40/ca.png', name: 'Canada' },
  { code: 'CM', dial: '+237', flagUrl: 'https://flagcdn.com/w40/cm.png', name: 'Cameroun' },
];

// ─── PHONE INPUT WITH FLAG ───────────────────────────────────────────
const PhoneInput = ({ value, onChange, onCountryChange, defaultCountry = 'CM', label, testId }) => {
  const [country, setCountry] = useState(() => COUNTRY_CODES.find(c => c.code === defaultCountry) || COUNTRY_CODES[1]);
  const [open, setOpen] = useState(false);

  const handleCountrySelect = (c) => {
    setCountry(c);
    setOpen(false);
    onChange('');
    if (onCountryChange) onCountryChange(c.code);
  };

  const handlePhoneChange = (e) => {
    const raw = e.target.value.replace(/\D/g, '');
    onChange(raw);
  };

  const fullPhone = value ? `${country.dial}${value}` : '';

  return (
    <div>
      {label && <label className="text-xs text-muted-foreground mb-1.5 block">{label}</label>}
      <div className="flex h-11 rounded-xl overflow-hidden border border-border focus-within:border-[#D4AF37]/50 transition-colors">
        {/* Country selector */}
        <div className="relative">
          <button
            type="button"
            className="h-full px-3 flex items-center gap-1.5 bg-muted/60 border-r border-border hover:bg-white/[0.12] transition-colors text-sm"
            onClick={() => setOpen(!open)}
            data-testid={`${testId}-country-btn`}
          >
            <img src={country.flagUrl} alt={country.name} width={20} height={15} style={{ borderRadius: '2px' }} />
            <span className="text-foreground/70 font-medium text-xs">{country.dial}</span>
            <svg className={`w-3 h-3 text-muted-foreground transition-transform ${open ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
          </button>
          {open && (
            <div className="absolute top-full left-0 mt-1 w-48 bg-card border border-border rounded-xl shadow-xl z-50 overflow-hidden">
              {COUNTRY_CODES.map(c => (
                <button
                  key={c.code}
                  type="button"
                  className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-sm hover:bg-muted/50 transition-colors ${country.code === c.code ? 'bg-[#D4AF37]/10 text-[#D4AF37]' : 'text-foreground/70'}`}
                  onClick={() => handleCountrySelect(c)}
                >
                  <img src={c.flagUrl} alt={c.name} width={20} height={15} style={{ borderRadius: '2px' }} />
                  <span className="font-medium">{c.name}</span>
                  <span className="ml-auto text-muted-foreground text-xs">{c.dial}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        {/* Phone number input */}
        <input
          type="text"
          inputMode="numeric"
          value={value}
          onChange={handlePhoneChange}
          placeholder="Numéro de téléphone"
          className="flex-1 h-full px-3 bg-muted/50 text-foreground placeholder:text-foreground/30 text-sm outline-none"
          data-testid={testId}
        />
      </div>
      <input type="hidden" value={fullPhone} data-testid={`${testId}-full`} />
    </div>
  );
};

// ─── HERO ────────────────────────────────────────────────────────────
const HeroSection = () => (
  <section className="relative overflow-hidden" data-testid="exchange-hero">
    <div className="absolute inset-0 bg-background" />
    <div className="absolute top-[-100px] right-[-50px] w-[500px] h-[500px] rounded-full bg-[#D4AF37]/[0.04] blur-[120px]" />

    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-6">
      <button
        onClick={() => window.history.back()}
        className="flex items-center gap-2 text-[#D4AF37]/70 hover:text-[#D4AF37] transition-colors mb-8 group"
        data-testid="exchange-back-btn"
      >
        <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
        <span className="text-sm tracking-wide uppercase">Retour</span>
      </button>

      <div className="max-w-2xl">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/20 text-[#D4AF37] text-xs font-medium mb-6">
          <Shield className="w-3.5 h-3.5" />
          Échange sécurisé par KACOS
        </div>
        <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground leading-[1.1] mb-4" data-testid="exchange-title">
          Échange de devises{' '}
          <span className="text-[#D4AF37]">CAD &harr; XAF</span>
        </h1>
        <p className="text-base text-muted-foreground max-w-lg">
          Taux compétitifs, transactions sécurisées. Aucun intermédiaire, KACOS gère tout.
        </p>
      </div>
    </div>
  </section>
);

// ─── RATE DISPLAY ────────────────────────────────────────────────────
const RateDisplay = ({ rates, loading }) => {
  if (loading || !rates) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 text-[#D4AF37] animate-spin" />
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8" data-testid="rate-display">
      <div className="p-4 rounded-xl bg-muted/30 border border-border text-center">
        <p className="text-[11px] text-muted-foreground uppercase tracking-wider mb-1">Taux du marché</p>
        <p className="font-heading text-xl font-bold text-foreground/70">
          1 CAD = {rates.market_rate} <span className="text-sm font-normal">XAF</span>
        </p>
      </div>
      <div className="p-4 rounded-xl bg-emerald-500/5 border border-emerald-500/15 text-center">
        <p className="text-[11px] text-emerald-400/70 uppercase tracking-wider mb-1">Transfert F CFA (XAF) → CAD</p>
        <p className="font-heading text-xl font-bold text-emerald-400" data-testid="buy-rate">
          1 CAD = {rates.buy_rate} <span className="text-sm font-normal">XAF</span>
        </p>
      </div>
      <div className="p-4 rounded-xl bg-primary/5 border border-primary/15 text-center">
        <p className="text-[11px] text-primary/70 uppercase tracking-wider mb-1">Transfert CAD → F CFA (XAF)</p>
        <p className="font-heading text-xl font-bold text-primary" data-testid="sell-rate">
          1 CAD = {rates.sell_rate} <span className="text-sm font-normal">XAF</span>
        </p>
      </div>
    </div>
  );
};

// ─── CONVERTER MODULE ────────────────────────────────────────────────
const ConverterModule = ({ rates, user, navigate, token, paymentInfo }) => {
  // Helpers to resolve KACOS payment coords (fallback to sensible defaults)
  const pi = paymentInfo || {};
  const kacos = {
    interacEmail: pi.interac_email || 'fabricengamaleu@gmail.com',
    interacQ: pi.interac_security_question || 'Transfert',
    interacA: pi.interac_security_answer || 'kacos',
    mmPhone: pi.mobile_money_phone || '+237 6 99 48 52 69',
    mmName: pi.mobile_money_name || 'Fabrice Ngamaleu',
    cashPhone: pi.cash_phone || '+237 6 97 08 26 81',
  };
  const [mode, setMode] = useState('buy_cad');
  const [sendAmount, setSendAmount] = useState('');
  const [receiveAmount, setReceiveAmount] = useState('');
  const [lastEdited, setLastEdited] = useState('send'); // 'send' or 'receive'
  const [submitting, setSubmitting] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [orderSuccess, setOrderSuccess] = useState(false);
  const [orderResult, setOrderResult] = useState(null);

  // Payment (how user pays)
  const [payMethod, setPayMethod] = useState('');
  // Receive (how user receives)
  const [receiveMethod, setReceiveMethod] = useState('');
  const [receiveName, setReceiveName] = useState('');
  const [receivePhone, setReceivePhone] = useState('');
  const [receivePhoneCountry, setReceivePhoneCountry] = useState('CM');
  const [receiveEmail, setReceiveEmail] = useState('');
  const [receiveSecQ, setReceiveSecQ] = useState('');
  const [receiveSecA, setReceiveSecA] = useState('');
  const [copiedDetails, setCopiedDetails] = useState(false);

  const rate = rates ? (mode === 'buy_cad' ? rates.buy_rate : rates.sell_rate) : 0;
  const isBuy = mode === 'buy_cad';
  const minOrder = rates?.min_order_cad || 10;
  const maxOrder = rates?.max_order_cad || 1000;

  // Dynamic currencies based on direction
  // F CFA → CAD: send XAF, receive CAD
  // CAD → F CFA: send CAD, receive XAF
  const sendCurrency = isBuy ? 'XAF' : 'CAD';
  const receiveCurrency = isBuy ? 'CAD' : 'XAF';

  // Compute amounts based on which field the user last edited
  let amountNum, amountXAF;
  if (isBuy) {
    // F CFA → CAD: user sends XAF, receives CAD
    if (lastEdited === 'send') {
      const xafVal = parseFloat(sendAmount) || 0;
      amountXAF = xafVal;
      amountNum = rate > 0 ? Math.round((xafVal / rate) * 100) / 100 : 0;
    } else {
      const cadVal = parseFloat(receiveAmount) || 0;
      amountNum = cadVal;
      amountXAF = Math.round(cadVal * rate);
    }
  } else {
    // CAD → F CFA: user sends CAD, receives XAF
    if (lastEdited === 'send') {
      const cadVal = parseFloat(sendAmount) || 0;
      amountNum = cadVal;
      amountXAF = Math.round(cadVal * rate);
    } else {
      const xafVal = parseFloat(receiveAmount) || 0;
      amountXAF = xafVal;
      amountNum = rate > 0 ? Math.round((xafVal / rate) * 100) / 100 : 0;
    }
  }

  const minSend = isBuy ? Math.round(minOrder * rate) : minOrder;
  const maxSend = isBuy ? Math.round(maxOrder * rate) : maxOrder;
  const isValid = amountNum >= minOrder && amountNum <= maxOrder;

  // Handle send amount change
  const handleSendChange = (val) => {
    setSendAmount(val);
    setLastEdited('send');
    // Auto-calculate receive
    const num = parseFloat(val) || 0;
    if (isBuy) {
      // XAF → CAD
      const cad = rate > 0 ? Math.round((num / rate) * 100) / 100 : 0;
      setReceiveAmount(cad > 0 ? String(cad) : '');
    } else {
      // CAD → XAF
      const xaf = Math.round(num * rate);
      setReceiveAmount(xaf > 0 ? String(xaf) : '');
    }
  };

  // Handle receive amount change (reverse calculation)
  const handleReceiveChange = (val) => {
    setReceiveAmount(val);
    setLastEdited('receive');
    // Auto-calculate send
    const num = parseFloat(val) || 0;
    if (isBuy) {
      // Receive CAD → compute XAF to send
      const xaf = Math.round(num * rate);
      setSendAmount(xaf > 0 ? String(xaf) : '');
    } else {
      // Receive XAF → compute CAD to send
      const cad = rate > 0 ? Math.round((num / rate) * 100) / 100 : 0;
      setSendAmount(cad > 0 ? String(cad) : '');
    }
  };

  // Reset payment fields when mode changes
  useEffect(() => {
    setSendAmount('');
    setReceiveAmount('');
    setLastEdited('send');
    setPayMethod('');
    setReceiveMethod('');
    setReceiveName('');
    setReceivePhone('');
    setReceiveEmail('');
    setReceiveSecQ('');
    setReceiveSecA('');
    setShowConfirm(false);
    setOrderSuccess(false);
  }, [mode]);

  // ── Validation: check all required fields per receive method ──
  const isFormComplete = (() => {
    if (!sendAmount || !isValid || !payMethod || !receiveMethod) return false;
    if (receiveMethod === 'interac_email') {
      return !!(receiveName?.trim() && receiveEmail?.trim() && receiveSecQ?.trim() && receiveSecA?.trim());
    }
    if (receiveMethod === 'interac_phone') {
      return !!(receiveName?.trim() && receivePhone?.trim() && receiveSecQ?.trim() && receiveSecA?.trim());
    }
    if (receiveMethod === 'orange_money' || receiveMethod === 'mtn_momo') {
      return !!(receiveName?.trim() && receivePhone?.trim());
    }
    if (receiveMethod === 'cash') return true;
    return false;
  })();

  const handleSubmit = async () => {
    if (!user) { navigate('/login'); return; }
    if (!isFormComplete) return;

    setSubmitting(true);
    try {
      // Build full phone number with country code
      let fullPhone = '';
      if (receivePhone) {
        const countryObj = COUNTRY_CODES.find(c => c.code === receivePhoneCountry) || COUNTRY_CODES[1];
        fullPhone = `${countryObj.dial}${receivePhone}`;
      }

      const payload = {
        order_type: mode,
        amount_cad: amountNum,
        pay_method: payMethod,
        receive_method: receiveMethod,
        receive_name: receiveName || undefined,
        receive_phone: fullPhone || undefined,
        receive_email: receiveEmail || undefined,
        receive_security_question: receiveSecQ || undefined,
        receive_security_answer: receiveSecA || undefined,
      };
      const res = await axios.post(`${API}/exchange/order`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrderResult({ ...res.data, pay_method: payMethod });
      setOrderSuccess(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la soumission');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Pay method options based on mode ──
  const payOptions = isBuy
    ? [
        { id: 'orange_money', label: 'Orange Money', icon: Smartphone },
        { id: 'mtn_momo', label: 'MTN MoMo', icon: Smartphone },
        { id: 'cash', label: 'Cash', icon: DollarSign },
      ]
    : [
        { id: 'interac', label: 'Interac', icon: CreditCard },
      ];

  // ── Receive method options based on mode ──
  const receiveOptions = isBuy
    ? [
        { id: 'interac_email', label: 'Interac (courriel)', icon: CreditCard },
        { id: 'interac_phone', label: 'Interac (téléphone)', icon: CreditCard },
      ]
    : [
        { id: 'orange_money', label: 'Orange Money', icon: Smartphone },
        { id: 'mtn_momo', label: 'MTN MoMo', icon: Smartphone },
        { id: 'cash', label: 'Cash', icon: DollarSign },
      ];

  const MethodButton = ({ opt, selected, onClick, testId }) => (
    <button
      className={`flex items-center gap-2 p-3 rounded-xl border text-sm transition-all ${
        selected === opt.id
          ? 'border-[#D4AF37]/50 bg-[#D4AF37]/10 text-[#D4AF37]'
          : 'border-border bg-muted/30 text-muted-foreground hover:border-white/20'
      }`}
      onClick={() => onClick(opt.id)}
      data-testid={testId}
    >
      <opt.icon className="w-4 h-4" />
      {opt.label}
    </button>
  );

  // ── Reset form after success ──
  const handleNewOrder = () => {
    setOrderSuccess(false);
    setShowConfirm(false);
    setSendAmount('');
    setReceiveAmount('');
    setLastEdited('send');
    setPayMethod('');
    setReceiveMethod('');
    setReceiveName('');
    setReceivePhone('');
    setReceiveEmail('');
    setReceiveSecQ('');
    setReceiveSecA('');
  };

  return (
    <div className="max-w-xl mx-auto" data-testid="converter-module">
      {/* ── SUCCESS SCREEN ── */}
      {orderSuccess ? (
        <div className="bg-muted/30 border border-border rounded-2xl p-6 sm:p-8 text-center" data-testid="order-success-screen">
          <div className="w-16 h-16 rounded-full bg-[#D4AF37]/20 flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-[#D4AF37]" />
          </div>
          <h2 className="font-heading text-xl font-bold text-foreground mb-1">Ordre créé — Procédez au paiement</h2>
          <p className="text-muted-foreground text-sm mb-5 max-w-md mx-auto">
            {isBuy ? "Achat" : "Vente"} de <span className="text-foreground font-semibold">{amountNum.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} CAD</span> ({amountXAF.toLocaleString('fr-FR')} XAF)
          </p>

          {/* 4h deadline banner */}
          <div className="flex items-start gap-2 bg-orange-500/10 border border-orange-500/20 rounded-xl px-4 py-3 mb-5 max-w-md mx-auto text-left">
            <Clock className="w-4 h-4 text-orange-400 shrink-0 mt-0.5" />
            <p className="text-sm text-orange-300">
              <strong>Délai de 4 heures</strong> — Votre ordre sera automatiquement annulé si le paiement n'est pas reçu à temps. Un rappel vous sera envoyé 1h avant l'expiration.
            </p>
          </div>

          {/* Payment instructions */}
          <div className="bg-muted/40 border border-[#D4AF37]/20 rounded-xl p-5 mb-5 text-left max-w-md mx-auto">
            <p className="text-sm text-[#D4AF37] font-semibold mb-3 flex items-center gap-2">
              <AlertCircle className="w-4 h-4" /> Instructions de paiement
            </p>
            {(orderResult?.pay_method === 'interac' || (!orderResult?.pay_method && !isBuy)) && (
              <div className="space-y-1.5 text-sm">
                <p className="text-muted-foreground text-xs uppercase tracking-wider mb-2">Envoyez votre virement Interac à :</p>
                <div className="flex justify-between"><span className="text-muted-foreground">Courriel</span><span className="text-foreground font-medium">{kacos.interacEmail}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Question</span><span className="text-foreground font-medium">{kacos.interacQ}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Mot de passe</span><span className="text-foreground font-medium">{kacos.interacA}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Montant exact</span><span className="text-[#D4AF37] font-bold">{amountNum} CAD</span></div>
              </div>
            )}
            {(orderResult?.pay_method === 'orange_money' || orderResult?.pay_method === 'mtn_momo') && (
              <div className="space-y-1.5 text-sm">
                <p className="text-muted-foreground text-xs uppercase tracking-wider mb-2">
                  Envoyez votre paiement {orderResult.pay_method === 'orange_money' ? 'Orange Money' : 'MTN MoMo'} à :
                </p>
                <div className="flex justify-between"><span className="text-muted-foreground">Numéro</span><span className="text-foreground font-medium">{kacos.mmPhone}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Nom</span><span className="text-foreground font-medium">{kacos.mmName}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Montant exact</span><span className="text-[#D4AF37] font-bold">{amountXAF.toLocaleString('fr-FR')} XAF</span></div>
              </div>
            )}
            {orderResult?.pay_method === 'cash' && (
              <p className="text-sm text-muted-foreground">Contactez KACOS au <span className="text-[#D4AF37] font-medium">{kacos.cashPhone}</span> pour organiser le paiement en espèces.</p>
            )}
            <p className="text-[11px] text-foreground/30 mt-3 pt-3 border-t border-border">
              Les instructions complètes ont été envoyées à votre adresse email.
            </p>
          </div>

          <div className="flex gap-3 max-w-md mx-auto">
            <Button
              variant="outline"
              className="flex-1 h-10 bg-muted/40 border-border text-muted-foreground rounded-xl hover:text-foreground text-sm"
              onClick={() => { setOrderSuccess(false); setOrderResult(null); handleNewOrder(); }}
              data-testid="new-order-btn"
            >
              Nouvel échange
            </Button>
            <Button
              className="flex-1 h-10 bg-[#D4AF37] hover:bg-[#C4A030] text-black font-semibold rounded-xl text-sm"
              onClick={() => navigate('/currency/transactions')}
              data-testid="go-transactions-btn"
            >
              Mes transactions
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      ) : (
      <>
      {/* Mode Toggle */}
      <div className="flex rounded-xl bg-muted/40 border border-border p-1 mb-8">
        <button
          className={`flex-1 py-3 px-4 rounded-lg text-sm font-semibold transition-all ${
            isBuy
              ? 'bg-[#D4AF37] text-black shadow-lg shadow-[#D4AF37]/20'
              : 'text-muted-foreground hover:text-foreground/70'
          }`}
          onClick={() => setMode('buy_cad')}
          data-testid="mode-buy-btn"
        >
          <DollarSign className="w-4 h-4 inline mr-1.5 -mt-0.5" />
          Transfert F CFA (XAF) → CAD
        </button>
        <button
          className={`flex-1 py-3 px-4 rounded-lg text-sm font-semibold transition-all ${
            !isBuy
              ? 'bg-[#D4AF37] text-black shadow-lg shadow-[#D4AF37]/20'
              : 'text-muted-foreground hover:text-foreground/70'
          }`}
          onClick={() => setMode('sell_cad')}
          data-testid="mode-sell-btn"
        >
          <Repeat className="w-4 h-4 inline mr-1.5 -mt-0.5" />
          Transfert CAD → F CFA (XAF)
        </button>
      </div>

      {/* Zero commission mention */}
      <p className="text-center text-sm font-semibold text-emerald-400 mt-3 mb-2" data-testid="zero-commission">0 commission en tout temps</p>

      {/* Converter Card */}
      <div className="bg-muted/30 border border-border rounded-2xl p-6 sm:p-8">
        {/* Rate info */}
        <div className="flex items-center justify-between mb-6">
          <span className="text-sm text-muted-foreground">Taux appliqué</span>
          <span className="font-heading font-semibold text-[#D4AF37]" data-testid="applied-rate">
            1 CAD = {rate} XAF
          </span>
        </div>

        {/* Amount Input */}
        <div className="space-y-4 mb-6">
          <div>
            <label className="text-xs text-muted-foreground uppercase tracking-wider mb-2 block">
              Vous envoyez
            </label>
            <div className="relative">
              <Input
                type="number"
                placeholder={`Min ${minSend.toLocaleString('fr-FR')} — Max ${maxSend.toLocaleString('fr-FR')}`}
                value={sendAmount}
                onChange={(e) => handleSendChange(e.target.value)}
                className="h-14 text-lg bg-muted/50 border-border text-foreground placeholder:text-foreground/30 focus:border-[#D4AF37]/50 focus:ring-[#D4AF37]/20 rounded-xl pr-16"
                data-testid="amount-send-input"
              />
              <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm font-semibold text-[#D4AF37]">{sendCurrency}</span>
            </div>
          </div>

          <div className="flex justify-center">
            <div className="w-10 h-10 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/20 flex items-center justify-center">
              <ArrowRight className="w-4 h-4 text-[#D4AF37] -rotate-90" />
            </div>
          </div>

          <div>
            <label className="text-xs text-muted-foreground uppercase tracking-wider mb-2 block">
              Vous recevez
            </label>
            <div className="relative">
              <Input
                type="number"
                placeholder="Montant souhaité"
                value={receiveAmount}
                onChange={(e) => handleReceiveChange(e.target.value)}
                className="h-14 text-lg bg-muted/50 border-border text-foreground placeholder:text-foreground/30 focus:border-[#D4AF37]/50 focus:ring-[#D4AF37]/20 rounded-xl pr-16"
                data-testid="amount-receive-input"
              />
              <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm font-semibold text-[#D4AF37]">{receiveCurrency}</span>
            </div>
          </div>
        </div>

        {/* Limits Warning */}
        {sendAmount && !isValid && (
          <div className="flex items-center gap-2 text-amber-400 text-xs bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2 mb-4" data-testid="limit-warning">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            <span>Montant entre {minSend.toLocaleString('fr-FR')} et {maxSend.toLocaleString('fr-FR')} {sendCurrency}</span>
          </div>
        )}

        {/* Expand payment details on confirm */}
        {!showConfirm ? (
          <Button
            className="w-full h-12 bg-[#D4AF37] hover:bg-[#C4A030] text-black font-semibold rounded-xl transition-all hover:scale-[1.01] active:scale-[0.99] shadow-lg shadow-[#D4AF37]/20 text-base"
            disabled={!sendAmount || !isValid}
            onClick={() => user ? setShowConfirm(true) : navigate('/login')}
            data-testid="continue-btn"
          >
            {user ? 'Continuer' : 'Connectez-vous pour continuer'}
          </Button>
        ) : (
          <div className="space-y-6 border-t border-border pt-6 mt-2" data-testid="payment-details">

            {/* ── SECTION 1: Je veux payer comment? ── */}
            <div data-testid="pay-section">
              <h4 className="font-heading font-semibold text-foreground text-sm mb-3 flex items-center gap-2">
                <CreditCard className="w-4 h-4 text-[#D4AF37]" />
                Je veux payer comment ?
              </h4>
              <div className={`grid gap-2 ${payOptions.length > 2 ? 'grid-cols-3' : payOptions.length === 2 ? 'grid-cols-2' : 'grid-cols-1'}`}>
                {payOptions.map(opt => (
                  <MethodButton
                    key={opt.id}
                    opt={opt}
                    selected={payMethod}
                    onClick={setPayMethod}
                    testId={`pay-${opt.id}`}
                  />
                ))}
              </div>

              {/* Pay info display */}
              {payMethod && (
                <div className="mt-3 p-3 rounded-xl bg-[#D4AF37]/5 border border-[#D4AF37]/15" data-testid="pay-info">
                  {payMethod === 'interac' && (
                    <div className="space-y-1.5 text-sm">
                      <p className="text-[#D4AF37] font-medium text-xs uppercase tracking-wider mb-2">Envoyez votre virement Interac à :</p>
                      <div className="flex justify-between"><span className="text-muted-foreground">Courriel</span><span className="text-foreground font-medium">{kacos.interacEmail}</span></div>
                      <div className="flex justify-between"><span className="text-muted-foreground">Question de sécurité</span><span className="text-foreground font-medium">{kacos.interacQ}</span></div>
                      <div className="flex justify-between"><span className="text-muted-foreground">Mot de passe</span><span className="text-foreground font-medium">{kacos.interacA}</span></div>
                      {/* ── Action buttons ── */}
                      <div className="flex gap-2 pt-2 border-t border-border mt-2">
                        <button
                          type="button"
                          data-testid="copy-interac-details"
                          onClick={() => {
                            const text = [
                              `Virement Interac KACOS${amountNum > 0 ? ` — ${amountNum} CAD (${amountXAF.toLocaleString('fr-CA')} XAF)` : ''}`,
                              `Email: ${kacos.interacEmail}`,
                              `Question de sécurité: ${kacos.interacQ}`,
                              `Mot de passe: ${kacos.interacA}`,
                            ].join('\n');
                            navigator.clipboard.writeText(text).then(() => {
                              setCopiedDetails(true);
                              setTimeout(() => setCopiedDetails(false), 2500);
                            });
                          }}
                          className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-medium transition-all bg-muted/50 border border-border hover:border-[#D4AF37]/40 hover:bg-[#D4AF37]/10 text-foreground/70 hover:text-[#D4AF37]"
                        >
                          <Copy className="w-3.5 h-3.5" />
                          {copiedDetails ? 'Copié !' : 'Copier les détails'}
                        </button>
                        <a
                          data-testid="mailto-interac-reminder"
                          href={(() => {
                            const subject = encodeURIComponent(`Virement Interac KACOS${amountNum > 0 ? ` — ${amountNum} CAD` : ''}`);
                            const body = encodeURIComponent([
                              `Bonjour,`,
                              ``,
                              `Rappel pour votre virement Interac à KACOS :`,
                              ``,
                              `  Courriel destinataire : ${kacos.interacEmail}`,
                              `  Montant               : ${amountNum > 0 ? `${amountNum} CAD (≈ ${amountXAF.toLocaleString('fr-CA')} XAF)` : 'À renseigner'}`,
                              `  Question de sécurité  : ${kacos.interacQ}`,
                              `  Mot de passe          : ${kacos.interacA}`,
                              ``,
                              `Connectez-vous à votre portail bancaire, cliquez sur`,
                              `"Envoyer un virement Interac" et utilisez ces informations.`,
                              ``,
                              `— KACOS`,
                            ].join('\n'));
                            const to = encodeURIComponent(user?.email || '');
                            return `mailto:${to}?subject=${subject}&body=${body}`;
                          })()}
                          className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-medium transition-all bg-muted/50 border border-border hover:border-[#D4AF37]/40 hover:bg-[#D4AF37]/10 text-foreground/70 hover:text-[#D4AF37]"
                        >
                          <Mail className="w-3.5 h-3.5" />
                          M'envoyer un rappel
                        </a>
                      </div>
                    </div>
                  )}
                  {(payMethod === 'orange_money' || payMethod === 'mtn_momo') && (
                    <div className="space-y-1.5 text-sm">
                      <p className="text-[#D4AF37] font-medium text-xs uppercase tracking-wider mb-2">
                        Envoyez votre paiement {payMethod === 'orange_money' ? 'Orange Money' : 'MTN MoMo'} à :
                      </p>
                      <div className="flex justify-between"><span className="text-muted-foreground">Numéro</span><span className="text-foreground font-medium">{kacos.mmPhone}</span></div>
                      <div className="flex justify-between"><span className="text-muted-foreground">Nom</span><span className="text-foreground font-medium">{kacos.mmName}</span></div>
                    </div>
                  )}
                  {payMethod === 'cash' && (
                    <p className="text-sm text-muted-foreground">Contactez le support KACOS pour organiser le paiement en espèces, au numéro suivant : <span className="text-[#D4AF37] font-medium">{kacos.cashPhone}</span></p>
                  )}
                </div>
              )}
            </div>

            {/* ── SECTION 2: Je veux recevoir comment? ── */}
            <div data-testid="receive-section">
              <h4 className="font-heading font-semibold text-foreground text-sm mb-3 flex items-center gap-2">
                <Wallet className="w-4 h-4 text-[#D4AF37]" />
                Je veux recevoir comment ?
              </h4>
              <div className={`grid gap-2 ${receiveOptions.length > 2 ? 'grid-cols-3' : receiveOptions.length === 2 ? 'grid-cols-2' : 'grid-cols-1'}`}>
                {receiveOptions.map(opt => (
                  <MethodButton
                    key={opt.id}
                    opt={opt}
                    selected={receiveMethod}
                    onClick={(id) => { setReceiveMethod(id); setReceiveName(''); setReceivePhone(''); setReceiveEmail(''); setReceiveSecQ(''); setReceiveSecA(''); }}
                    testId={`receive-${opt.id}`}
                  />
                ))}
              </div>

              {/* Receive detail inputs */}
              {receiveMethod && (
                <div className="mt-3 space-y-3" data-testid="receive-fields">
                  {receiveMethod === 'interac_email' && (
                    <>
                      <div>
                        <label className="text-xs text-muted-foreground mb-1.5 block">Nom du récipiendaire</label>
                        <Input value={receiveName} onChange={(e) => setReceiveName(e.target.value)}
                          placeholder="Nom complet" className="h-11 bg-muted/50 border-border text-foreground placeholder:text-foreground/30 rounded-xl"
                          data-testid="receive-name-input" />
                      </div>
                      <div>
                        <label className="text-xs text-muted-foreground mb-1.5 block">Courriel du récipiendaire</label>
                        <Input value={receiveEmail} onChange={(e) => setReceiveEmail(e.target.value)}
                          placeholder="adresse@email.com" className="h-11 bg-muted/50 border-border text-foreground placeholder:text-foreground/30 rounded-xl"
                          data-testid="receive-email-input" />
                      </div>
                      <div>
                        <label className="text-xs text-muted-foreground mb-1.5 block">Question de sécurité</label>
                        <Input value={receiveSecQ} onChange={(e) => setReceiveSecQ(e.target.value)}
                          placeholder="Ex: Quel est le nom de mon animal ?" className="h-11 bg-muted/50 border-border text-foreground placeholder:text-foreground/30 rounded-xl"
                          data-testid="receive-sec-q-input" />
                      </div>
                      <div>
                        <label className="text-xs text-muted-foreground mb-1.5 block">Mot de passe (réponse)</label>
                        <Input value={receiveSecA} onChange={(e) => setReceiveSecA(e.target.value)}
                          placeholder="Réponse à la question de sécurité" className="h-11 bg-muted/50 border-border text-foreground placeholder:text-foreground/30 rounded-xl"
                          data-testid="receive-sec-a-input" />
                      </div>
                    </>
                  )}
                  {receiveMethod === 'interac_phone' && (
                    <>
                      <div>
                        <label className="text-xs text-muted-foreground mb-1.5 block">Nom du récipiendaire</label>
                        <Input value={receiveName} onChange={(e) => setReceiveName(e.target.value)}
                          placeholder="Nom complet" className="h-11 bg-muted/50 border-border text-foreground placeholder:text-foreground/30 rounded-xl"
                          data-testid="receive-name-input" />
                      </div>
                      <PhoneInput
                        value={receivePhone}
                        onChange={(val) => setReceivePhone(val)}
                        onCountryChange={(code) => setReceivePhoneCountry(code)}
                        defaultCountry="CA"
                        label="Numéro de téléphone"
                        testId="receive-phone-input"
                      />
                      <div>
                        <label className="text-xs text-muted-foreground mb-1.5 block">Question de sécurité</label>
                        <Input value={receiveSecQ} onChange={(e) => setReceiveSecQ(e.target.value)}
                          placeholder="Ex: Quel est le nom de mon animal ?" className="h-11 bg-muted/50 border-border text-foreground placeholder:text-foreground/30 rounded-xl"
                          data-testid="receive-sec-q-input" />
                      </div>
                      <div>
                        <label className="text-xs text-muted-foreground mb-1.5 block">Mot de passe (réponse)</label>
                        <Input value={receiveSecA} onChange={(e) => setReceiveSecA(e.target.value)}
                          placeholder="Réponse à la question de sécurité" className="h-11 bg-muted/50 border-border text-foreground placeholder:text-foreground/30 rounded-xl"
                          data-testid="receive-sec-a-input" />
                      </div>
                    </>
                  )}
                  {(receiveMethod === 'orange_money' || receiveMethod === 'mtn_momo') && (
                    <>
                      <div>
                        <label className="text-xs text-muted-foreground mb-1.5 block">Nom du récipiendaire</label>
                        <Input value={receiveName} onChange={(e) => setReceiveName(e.target.value)}
                          placeholder="Nom complet" className="h-11 bg-muted/50 border-border text-foreground placeholder:text-foreground/30 rounded-xl"
                          data-testid="receive-name-input" />
                      </div>
                      <PhoneInput
                        value={receivePhone}
                        onChange={(val) => setReceivePhone(val)}
                        onCountryChange={(code) => setReceivePhoneCountry(code)}
                        defaultCountry="CM"
                        label={`Numéro ${receiveMethod === 'orange_money' ? 'Orange Money' : 'MTN MoMo'}`}
                        testId="receive-phone-input"
                      />
                    </>
                  )}
                  {receiveMethod === 'cash' && (
                    <p className="text-sm text-muted-foreground bg-muted/30 border border-border rounded-xl p-3">
                      Contactez le support KACOS pour organiser la réception en espèces, au numéro suivant : <span className="text-[#D4AF37] font-medium">{kacos.cashPhone}</span>
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Summary */}
            <div className="bg-white/[0.02] border border-border rounded-xl p-4 space-y-2 text-sm" data-testid="order-summary">
              <div className="flex justify-between text-muted-foreground">
                <span>Type</span>
                <span className="text-foreground font-medium">{isBuy ? 'Transfert F CFA → CAD' : 'Transfert CAD → F CFA'}</span>
              </div>
              <div className="flex justify-between text-muted-foreground">
                <span>Vous envoyez</span>
                <span className="text-foreground font-medium">
                  {isBuy
                    ? `${amountXAF.toLocaleString('fr-FR')} XAF`
                    : `${amountNum.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} CAD`
                  }
                </span>
              </div>
              <div className="flex justify-between text-muted-foreground">
                <span>Taux</span>
                <span className="text-foreground font-medium">{rate} XAF/CAD</span>
              </div>
              <div className="flex justify-between text-muted-foreground pt-2 border-t border-border">
                <span className="font-medium">Vous recevez</span>
                <span className="text-[#D4AF37] font-heading font-bold text-base">
                  {isBuy
                    ? `${amountNum.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} CAD`
                    : `${amountXAF.toLocaleString('fr-FR')} XAF`
                  }
                </span>
              </div>
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1 h-11 bg-muted/40 border-border text-muted-foreground rounded-xl"
                onClick={() => setShowConfirm(false)}
                data-testid="cancel-confirm-btn"
              >
                Retour
              </Button>
              <Button
                className="flex-1 h-11 bg-[#D4AF37] hover:bg-[#C4A030] text-black font-semibold rounded-xl transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                onClick={handleSubmit}
                disabled={submitting || !isFormComplete}
                data-testid="submit-order-btn"
              >
                {submitting ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Envoi...</>
                ) : (
                  "Confirmer l'ordre"
                )}
              </Button>
            </div>
          </div>
        )}
      </div>
      </>
      )}
    </div>
  );
};

// ─── PAYMENT METHODS INFO ────────────────────────────────────────────
const PaymentMethodsSection = () => (
  <section className="py-16 border-t border-border" data-testid="payment-methods-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-10">
        <h2 className="font-heading text-xl sm:text-2xl font-bold text-foreground mb-2">
          Modes de <span className="text-[#D4AF37]">paiement</span>
        </h2>
        <p className="text-sm text-muted-foreground">Choisissez le mode qui vous convient</p>
      </div>
      <div className="grid sm:grid-cols-2 gap-5 max-w-2xl mx-auto">
        <div className="p-5 rounded-2xl bg-white/[0.02] border border-border">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center">
              <CreditCard className="w-5 h-5 text-[#D4AF37]" />
            </div>
            <div>
              <h3 className="font-heading font-semibold text-foreground text-sm">Virement Interac</h3>
              <p className="text-[11px] text-muted-foreground">Canada</p>
            </div>
          </div>
          <p className="text-xs text-muted-foreground leading-relaxed">Transférez par courriel ou téléphone. Rapide et sécurisé pour les transactions au Canada.</p>
        </div>
        <div className="p-5 rounded-2xl bg-white/[0.02] border border-border">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center">
              <Smartphone className="w-5 h-5 text-[#D4AF37]" />
            </div>
            <div>
              <h3 className="font-heading font-semibold text-foreground text-sm">Mobile Money</h3>
              <p className="text-[11px] text-muted-foreground">Cameroun</p>
            </div>
          </div>
          <p className="text-xs text-muted-foreground leading-relaxed">Recevez directement sur votre compte Mobile Money. Orange Money, MTN MoMo acceptés.</p>
        </div>
      </div>
    </div>
  </section>
);

// ─── TRUST SECTION ───────────────────────────────────────────────────
const TrustSection = () => (
  <section className="py-16 border-t border-border" data-testid="exchange-trust-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-12">
        <h2 className="font-heading text-xl sm:text-2xl font-bold text-foreground mb-2">
          Échange en toute <span className="text-[#D4AF37]">confiance</span>
        </h2>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
        {[
          { icon: Shield, title: 'Transactions sécurisées', desc: 'Chaque opération est validée et suivie par KACOS.' },
          { icon: TrendingUp, title: 'Taux compétitifs', desc: 'Taux mis à jour en continu basé sur le marché.' },
          { icon: BadgeCheck, title: 'Aucun contact direct', desc: 'KACOS gère tout. Aucun échange entre utilisateurs.' },
          { icon: Headphones, title: 'Support client', desc: 'Assistance rapide pour toute question.' },
        ].map(({ icon: Icon, title, desc }) => (
          <div key={title} className="group p-5 rounded-2xl bg-white/[0.02] border border-border hover:border-[#D4AF37]/20 transition-all">
            <div className="w-10 h-10 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center mb-3 group-hover:bg-[#D4AF37]/15 transition-colors">
              <Icon className="w-5 h-5 text-[#D4AF37]" />
            </div>
            <h3 className="font-heading font-semibold text-foreground text-sm mb-1">{title}</h3>
            <p className="text-xs text-muted-foreground leading-relaxed">{desc}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// ─── MAIN PAGE ───────────────────────────────────────────────────────
export default function CurrencyListingsPage() {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [rates, setRates] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paymentInfo, setPaymentInfo] = useState(null);

  const fetchRates = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/exchange/rate`);
      setRates(res.data);
    } catch (err) {
      console.error('Failed to fetch rates:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchPaymentInfo = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/exchange/payment-info`);
      setPaymentInfo(res.data);
    } catch (err) {
      console.error('Failed to fetch payment info:', err);
    }
  }, []);

  useEffect(() => {
    fetchRates();
    fetchPaymentInfo();
    const interval = setInterval(fetchRates, 60000);
    return () => clearInterval(interval);
  }, [fetchRates, fetchPaymentInfo]);

  return (
    <div className="min-h-screen bg-background" data-testid="exchange-page">
      <Navbar />

      <HeroSection />

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <RateDisplay rates={rates} loading={loading} />
        <ConverterModule rates={rates} user={user} navigate={navigate} token={token} paymentInfo={paymentInfo} />
      </section>

      <PaymentMethodsSection />
      <TrustSection />

      <Footer />
    </div>
  );
}
